import React, { useState } from "react";
import { Button, Card, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup"; // Import Yup for validation
import { signUpParticipant } from "../../Common/data/signUpParticipant"; // Import the API function
import AuthContent from "./AuthContent";

import logolight from "../../assets/images/logo-light.png";

const CreateAccount = () => {
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    const validationSchema = Yup.object().shape({
        fullName: Yup.string().required("Full name is required"),
        email: Yup.string().email("Invalid email address").required("Email is required"),
        password: Yup.string().min(6, "Password must be at least 6 characters").required("Password is required"),
        agreeTerms: Yup.boolean().oneOf([true], "You must agree to the terms and conditions"),
    });

    const formik = useFormik({
        initialValues: {
            fullName: "",
            email: "",
            password: "",
            agreeTerms: false,
        },
        validationSchema,
        onSubmit: async (values) => {
            try {
                setIsLoading(true); // Set loading state
                setError(null); // Clear any previous errors
        
                // Call the signUpParticipant API function
                const response = await signUpParticipant(values.fullName, values.email, values.password);
                console.log("Response from API:", response); // Log the response
        
                // Redirect to another page after successful sign-up
                navigate("/emailverify");
            } catch (error: any) {
                if (error.response && error.response.status === 400) {
                    setError("User with this email already exists."); // Specific message for 400 error
                } else {
                    setError(error.message || "An unexpected error occurred. Please try again."); // Generic error message
                }
            } finally {
                setIsLoading(false); // Reset loading state
            }
        }        
    });

    const handleTogglePassword = () => {
        setPasswordVisible((prevVisible) => !prevVisible);
    };

    return (
        <React.Fragment>
            <section className="position-relative min-vh-100 d-flex align-items-center justify-content-center">
                <Container fluid>
                    <Row className="justify-content-center">
                        {window.innerWidth > 768 ? <AuthContent /> : null}
                        <Col xs={12} md={6}>
                            <div className="auth-card">
                                <Card className="border-0 mb-0">
                                    <Card.Body className="px-md-5">
                                        <img src={logolight} height={100} />
                                        <h2 className="mb-4">Register</h2>
                                        <Form onSubmit={formik.handleSubmit}>
                                            <Form.Group controlId="fullName" className="mb-3">
                                                <Form.Label>Full Name</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter your full name"
                                                    value={formik.values.fullName}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    isInvalid={formik.touched.fullName && !!formik.errors.fullName}
                                                />
                                                <Form.Control.Feedback type="invalid">{formik.errors.fullName}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Group controlId="email" className="mb-3">
                                                <Form.Label>Email</Form.Label>
                                                <Form.Control
                                                    type="email"
                                                    placeholder="Enter your email"
                                                    value={formik.values.email}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    isInvalid={formik.touched.email && !!formik.errors.email}
                                                />
                                                <Form.Control.Feedback type="invalid">{formik.errors.email}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Group controlId="password" className="mb-3 position-relative">
                                                <Form.Label>Password</Form.Label>
                                                <div className="password-toggle">
                                                    <Form.Control
                                                        type={passwordVisible ? "text" : "password"}
                                                        placeholder="Enter your password"
                                                        value={formik.values.password}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        isInvalid={formik.touched.password && !!formik.errors.password}
                                                    />
                                                    <Button
                                                        className="btn btn-link position-absolute end-0 bottom-0 text-decoration-none text-muted password-addon"
                                                        id="password-addon"
                                                        onClick={handleTogglePassword}
                                                    >
                                                        <i className={passwordVisible ? "ri-eye-fill align-middle" : "ri-eye-off-fill align-middle"} />
                                                    </Button>
                                                </div>
                                                <Form.Control.Feedback type="invalid">{formik.errors.password}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Group controlId="agreeTerms" className="mb-4">
                                                <Form.Check
                                                    type="checkbox"
                                                    label={
                                                        <span>
                                                            I agree to the <Link to="/terms-of-service">Terms of Service</Link> and{" "}
                                                            <Link to="/privacy-policy">Privacy Policy</Link>
                                                        </span>
                                                    }
                                                    checked={formik.values.agreeTerms}
                                                    onChange={formik.handleChange}
                                                    isInvalid={formik.touched.agreeTerms && !!formik.errors.agreeTerms}
                                                />
                                                <Form.Control.Feedback type="invalid">{formik.errors.agreeTerms}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Button variant="primary" type="submit" disabled={!formik.isValid || isLoading} className="w-100">
                                                {isLoading ? (
                                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                ) : (
                                                    "Create Account"
                                                )}
                                            </Button>
                                            {error && <p className="text-danger mt-2">{error}</p>}
                                            <div className="mt-3 text-center">
                                                <p>
                                                    Already have an account? <Link to="/signin">Sign In</Link>
                                                </p>
                                            </div>
                                        </Form>
                                    </Card.Body>
                                </Card>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </React.Fragment>
    );
};

export default CreateAccount;
